import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FiEdit2, FiTrash2, FiSearch } from "react-icons/fi";

export default function DashboardOverview() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    drafts: 0,
    users: 0,
  });


  const [recentPosts, setRecentPosts] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const postsRef = collection(db, "posts");

        const usersRef = collection(db, "users");

        const [
          allSnap,
          publishedSnap,
          draftSnap,
          recentSnap,
          usersSnap,
        ] = await Promise.all([
          getDocs(postsRef),
          getDocs(query(postsRef, where("status", "==", "published"))),
          getDocs(query(postsRef, where("status", "==", "draft"))),
          getDocs(query(postsRef, orderBy("createdAt", "desc"), limit(6))),
          getDocs(usersRef),
        ]);


        setStats({
          total: allSnap.size,
          published: publishedSnap.size,
          drafts: draftSnap.size,
          users: usersSnap.size,
        });


        setRecentPosts(
          recentSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
      } catch (err) {
        console.error("Dashboard fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredPosts = recentPosts.filter((p) =>
    p.title?.toLowerCase().includes(search.toLowerCase())
  );

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;

    await deleteDoc(doc(db, "posts", id));
    setRecentPosts((prev) => prev.filter((p) => p.id !== id));
    setStats((s) => ({
      ...s,
      total: s.total - 1,
    }));
  };

  if (loading) {
    return <p className="text-neutral-400">Loading dashboard...</p>;
  }

  return (
    <div className="space-y-8">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Dashboard Overview</h2>
          <p className="text-sm text-neutral-400">Welcome back, Admin</p>
        </div>

        <button
          onClick={() => navigate("/create")}
          className="px-5 py-2 rounded-lg bg-red-500 text-black font-semibold hover:opacity-90"
        >
          + Create Post
        </button>
      </div>

      {/* STATS */}
     <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
  <Stat title="Total Posts" value={stats.total} />
  <Stat title="Published" value={stats.published} />
  <Stat title="Drafts" value={stats.drafts} />
  <Stat title="Users" value={stats.users} />
</div>

      {/* RECENT POSTS */}
      <div className="bg-neutral-900 rounded-xl p-6">

        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Recent Posts</h3>

          <div className="relative w-72">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 text-sm" />
            <input
              type="text"
              placeholder="Search posts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-neutral-950
                         border border-neutral-800 text-sm
                         focus:border-red-500 outline-none"
            />
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="text-neutral-400">
            <tr>
              <th className="text-left py-3">Title</th>
              <th className="text-left py-3">Category</th>
              <th className="text-left py-3">Status</th>
              <th className="text-right py-3">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredPosts.length === 0 ? (
              <tr>
                <td colSpan="4" className="py-6 text-center text-neutral-500">
                  No posts found
                </td>
              </tr>
            ) : (
              filteredPosts.map((post) => (
                <tr
                  key={post.id}
                  className="border-t border-neutral-800 hover:bg-neutral-800/40"
                >
                  <td className="py-3 truncate max-w-xs">{post.title}</td>

                  <td className="py-3">
                    {post.category?.name || "—"}
                  </td>

                  <td className="py-3">
                    <span
                      className={`text-xs font-medium ${post.status === "published"
                        ? "text-emerald-400"
                        : "text-rose-400"
                        }`}
                    >
                      {post.status}
                    </span>
                  </td>

                  <td className="py-3 flex justify-end gap-4 text-neutral-400">
                    <FiEdit2
                      className="cursor-pointer hover:text-white"
                      onClick={() => navigate(`/posts?edit=${post.id}`)}
                    />
                    <FiTrash2
                      className="cursor-pointer hover:text-red-500"
                      onClick={() => deletePost(post.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="bg-neutral-900 rounded-xl p-5">
      <p className="text-sm text-neutral-400">{title}</p>
      <h3 className="text-2xl font-semibold mt-2">{value}</h3>
    </div>
  );
}
